// @flow
import * as React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { connect } from "react-redux";
import {
  branch,
  compose,
  renderComponent,
  withHandlers,
  withProps,
} from "recompose";
import {
  anyPass,
  applySpec,
  drop,
  equals,
  groupBy,
  indexBy,
  isEmpty,
  keys,
  map,
  partition,
  path,
  pathEq,
  pathOr,
  pipe,
  prop,
  range,
  take,
  toPairs,
  uniq,
  filter as filterRamda,
  all,
  trim,
  pick,
  isNil,
  hasPath,
} from "ramda";
import { Maybe } from "ramda-fantasy";
import moment from "moment";

import { withI18n, type I18n } from "@quantum/i18n";
import { Spinner, withModalTrigger } from "@quantum/ui";

import { ClipboardModule } from "@ag-grid-enterprise/all-modules";

import {
  aggregationFunctions,
  CASA_DECIMAL_PADRAO_NORMAL,
  CASA_DECIMAL_PADRAO_SERIES,
  decodeAssetKey,
  defaultColDef,
  IDENTIFICADOR_SERIE_CNPJ,
  IDENTIFICADOR_SERIE_CODIGO,
  IDENTIFICADOR_SERIE_NOME,
  MODO_APP_SERIES,
  MODO_EXIBICAO_MEDIDA_COMPOSTA,
  type AssetKey,
  type ReportCol,
  type ReportGridRow,
  type ReportId,
} from "../../../model";

import {
  exportReportAsCSV,
  exportReportAsExcel,
  exportarExcelVinculado,
  getGridState,
  getIsAxis,
  identificadoresSeries,
  openAssetMenu,
  openGlobalSearch,
  openNewTask,
  removeAssets,
  saveAssetGroup,
  sendMail,
  exibirDatasSemNegociacaoSeries,
  getModoApp,
  getEscalaMoedas,
  i18n,
} from "../../../state";

import {
  isGeneratingReport,
  isLoadingSelectedReport,
  hasLoadedSelectedReport,
  saveGridState,
  selectedReportCols,
  selectedReportGridRows,
  selectedReportId,
} from "../../../state";

import {
  parseDate,
  isEmptyLine,
  maybeEmpty,
  maybeParseFloat,
  formatCNPJ,
  formatDate,
  formatNumber,
  getAssetsWithoutResults,
  removePad,
  escalaFormatacao,
} from "../../../util";

import { AgGridReact } from "ag-grid-react";
import { Filters } from "../";
import Header from "../header";

import AggregationStatusBar from "./aggregation-status-bar";
import DatePicker from "./date-picker";
import FormattedNumberFilter from "./formatted-number-filter";
import ProxyFloatingFilter from "./proxy-floating-filter";

import ChartTooltip from "./chart-tooltip";
import { createColumn, createDateColumn } from "./columns";

import "./grid.css";

type Props = {
  loaded: boolean,
  loading: boolean,
  cols: Array<ReportCol>,
  rows: Array<ReportGridRow>,
  colHasMenuGroupItem: number => boolean,
  reportId: ReportId,
  onOpenNewTask: Function,
  onRemoveAsset: AssetKey => void,
  onRemoveAssets: (Array<AssetKey>) => void,
};

type State = {
  currentPage: number,
  pageCount: number,
  pageSize: number,
  pagination: boolean,
  selectedAssets: Array<AssetKey>,
};

// ag-grid infelizmente não suporta símbolos
const colType = "__colType__";

const encodeColumnKey = assetKey => {
  const { id, type } = decodeAssetKey(assetKey);
  // Usamos um separador meio aleatório porque ativos personalizados podem ter nomes que
  // conflitam com o separador.
  // Ps: não podemos usar caracteres unicode porque a API de visualização não os aceitam.
  return `${id}-#%*-${type}`;
};

const enhance = compose(
  withI18n,
  withModalTrigger,

  connect(
    applySpec({
      cols: selectedReportCols,
      rows: selectedReportGridRows,
      gridState: getGridState,
      reportId: selectedReportId,
      loading: isLoadingSelectedReport,
      generating: isGeneratingReport,
      loaded: hasLoadedSelectedReport,
      isInsideAxis: getIsAxis,
      identificadores: compose(ids => new Set(ids), identificadoresSeries),
      exibirDatasSemNegociacao: exibirDatasSemNegociacaoSeries,
      escalasMoedas: getEscalaMoedas,
      i18n,
      modoApp: getModoApp,
    }),
    {
      exportReportAsCSV,
      exportReportAsExcel,
      exportarExcelVinculado,
      removeAssets,
      saveAssetGroup,
      sendMail,
      onOpenAssetMenu: openAssetMenu,
      onOpenNewTask: openNewTask,
      openGlobalSearch,
      saveGridState,
    }
  ),

  branch(
    anyPass([prop("loading"), prop("generating")]),
    renderComponent(() => (
      <div className="Grid Grid__loading">
        <Spinner />
      </div>
    ))
  ),

  withHandlers({
    onOpenGlobalSearchLinkClick: ({ openGlobalSearch }) => event =>
      openGlobalSearch(event.target.value),
    onRemoveAsset: ({ removeAssets, reportId }) => assetKey =>
      removeAssets(reportId, [assetKey]),
    onRemoveAssets: ({ removeAssets, reportId }) => assetKeys =>
      removeAssets(reportId, assetKeys),
    colHasMenuGroupItem: ({ cols }) => colIndex =>
      pathEq([colIndex, "type"], "string", cols),
  }),

  withProps(
    ({
      cols: reportCols,
      rows: reportRows,
      identificadores,
      exibirDatasSemNegociacao,
      escalasMoedas,
      modoApp,
      i18n,
    }) => {
      const ativosComResultadoVazio = getAssetsWithoutResults(reportRows, 4);

      const casasDecimaisDefault =
        modoApp === MODO_APP_SERIES
          ? CASA_DECIMAL_PADRAO_SERIES
          : CASA_DECIMAL_PADRAO_NORMAL;

      const linhasAtivosComResultado = filterRamda(
        row => isNil(ativosComResultadoVazio[row.key]),
        reportRows
      );

      const linhasPorDatas = filterRamda(
        linhas => {
          const isDataSemResultado = all(
            ({ values }) => isEmptyLine(drop(4, values)),
            linhas
          );
          return exibirDatasSemNegociacao || !isDataSemResultado;
        },
        groupBy(row => row.values[3], linhasAtivosComResultado)
      );

      const ativos = filterRamda(
        ativo => isNil(ativosComResultadoVazio[ativo]),
        uniq(reportRows.map(row => row.key))
      );
      const datas = uniq(keys(linhasPorDatas)).sort(
        (a, b) =>
          parseDate(a, "DD/MM/YYYY").getOrElse(0) -
          parseDate(b, "DD/MM/YYYY").getOrElse(0)
      );

      const numeroDeMedidas = drop(4, reportCols).length;

      const rows = datas.map(data => {
        const dadosPorAtivos = indexBy(prop("key"), linhasPorDatas[data]);
        return {
          rowId: data,
          values: [
            parseDate(data, "DD/MM/YYYY").getOrElse(null),
            ...ativos.flatMap(key => {
              const dadosDoAtivo = dadosPorAtivos[key];
              return isNil(dadosDoAtivo)
                ? new Array(numeroDeMedidas).fill(null)
                : drop(4, dadosDoAtivo.values);
            }),
          ],
        };
      });

      const geraEscalaNaDescricao = medida => {
        const escala = pathOr(null, ["escala"], medida);
        const modoExibicao = pathOr(
          null,
          ["medida", "medida", "modoExibicao"],
          medida
        );

        return modoExibicao === MODO_EXIBICAO_MEDIDA_COMPOSTA ||
          isNil(escala) ||
          escala === 0
          ? ""
          : " " +
              i18n.translate("preposicaoEm") +
              " " +
              i18n.translate(escalasMoedas[escala].chaveTraducao);
      };

      const headers = ativos.map(ativo => {
        const [codigo, cnpj, nome] = take(
          3,
          reportRows.find(row => row.key === ativo).values
        );

        const selectedIds = [
          identificadores.has(IDENTIFICADOR_SERIE_CODIGO)
            ? maybeEmpty(codigo).getOrElse("-")
            : null,
          identificadores.has(IDENTIFICADOR_SERIE_CNPJ)
            ? maybeEmpty(cnpj)
                .map(formatCNPJ)
                .getOrElse("-")
            : null,
          identificadores.has(IDENTIFICADOR_SERIE_NOME) ? nome : null,
        ].filter(Boolean);

        // Nós temos uma lista de identificadores que queremos exibir no header (ex: Nome, CNPJ, Codigo),
        // porém o ag-grid precisa de uma estrutura em árvore, ou seja, essa função transforma
        // ["Nome", "CNPJ", "Codigo"] em:
        // {
        //   headerName: "Nome",
        //   children: [{
        //     headerName: "CNPJ",
        //     children: [{
        //       headerName: "Codigo",
        //       children: [...colunas do relatorio]
        //     }],
        //   }],
        // }
        const buildHeader = (ids, cols = []) => {
          const [id, ...rest] = ids;
          return {
            headerName: id,
            children: rest.length > 0 ? [buildHeader(rest, cols)] : cols,
          };
        };

        return {
          key: ativo,
          codigo,
          cnpj,
          nome,
          buildHeader: cols => buildHeader(selectedIds, cols),
        };
      });

      const [dateCol, ...assetCols] = drop(3, reportCols);

      const cols = [
        {
          ...createDateColumn(path(["data", "values", 0]), dateCol),
          headerName: i18n.translate("Periodo.Data"),
          field: String(dateCol.alias || 0),
          chartDataType: "category",
          rowGroup: false,
          enableValue: true,
          sortable: true,
          cellClass: "Grid__cell--centered",
          minWidth: 50,
          valueFormatter: params => moment(params.value).format("DD/MM/YYYY"),
          [colType]: dateCol.type,
        },
        ...headers.map(({ key, nome, buildHeader }, i, allHeaders) =>
          buildHeader(
            assetCols.map((col, j) => {
              const index = 1 + i * assetCols.length + j;
              return {
                ...createColumn(
                  path(["data", "values", index]),
                  col,
                  casasDecimaisDefault,
                  i18n.translate
                ),
                // O getter para exibir na grid o nome da coluna
                headerValueGetter: () => col.label + geraEscalaNaDescricao(col),
                // E o headerName apenas como legenda dos gráficos
                headerName: `${col.label +
                  geraEscalaNaDescricao(col)} - ${nome}`,
                // Estamos concatenando o alias da coluna com o codigo do ativo, de forma
                // podermos salvar na grid os estados de filtro/ordenação dessas colunas.
                field: String(
                  `${col.alias || index}---${encodeColumnKey(key)}`
                ),
                chartDataType: "series",
                rowGroup: false,
                minWidth: 50,
                cellClass: "Grid__cell--centered",
                [colType]: col.type,
                cellRendererFramework: params => {
                  const { value, valueFormatted } = params;

                  // Formata a célula aqui caso o valor passado possua __metadados__
                  // Usado somente em casos em que a formatação
                  // da coluna não se aplica especificamente para essa célula.
                  // Importante: Se as regras de formatação de coluna mudarem, aqui também
                  //             deve sofrer alterações!

                  const parseValueOrJson = testedVar => {
                    try {
                      return testedVar.__metadados__;
                    } catch {
                      return value;
                    }
                  };

                  const normalizaValor = ({ valor, formatacaoResultado }) => {
                    switch (formatacaoResultado.formatacao) {
                      case "PORCENTAGEM_TAXAS":
                        return maybeEmpty(String(valor).trim())
                          .map(value =>
                            value
                              .replace("%", "")
                              .replace(/\./g, "")
                              .replace(",", ".")
                          )
                          .map(value =>
                            maybeParseFloat(value)
                              .map(number => String(number / 100))
                              .getOrElse(value)
                          )
                          .getOrElse(null);

                      case "CNPJ":
                        return maybeEmpty(valor)
                          .map(s => String(s).trim())
                          .map(formatCNPJ)
                          .getOrElse(null);

                      case "DATA":
                        return maybeEmpty(valor)
                          .map(trim)
                          .chain(date => parseDate(date))
                          .map(date => formatDate(date))
                          .getOrElse(
                            maybeEmpty(value)
                              .map(trim)
                              .getOrElse(null)
                          );

                      default:
                        return maybeEmpty(valor)
                          .map(s => String(s).trim())
                          .getOrElse(null);
                    }
                  };

                  const onlyNumbers = number =>
                    isNaN(number) ? Maybe.Nothing() : Maybe(number);

                  const formataMoeda = (valor, formatacaoResultado) => {
                    const decimals =
                      hasPath(["casasDecimais"], col) &&
                      !isNil(col.casasDecimais)
                        ? col.casasDecimais
                        : casasDecimaisDefault;

                    const moneySymbol = code => {
                      const moneySymbols = {
                        BRL: "R$",
                        EUR: "€",
                        USD: "$",
                      };

                      return moneySymbols[code];
                    };

                    return maybeEmpty(valor)
                      .map(parseFloat)
                      .chain(onlyNumbers)
                      .map(number => number.toString())
                      .map(number =>
                        formatNumber(
                          {
                            ...pick(
                              ["decimals", "prefix", "suffix"],
                              formatacaoResultado
                            ),
                            decimals,
                            prefix: maybeEmpty(
                              moneySymbol(formatacaoResultado.moeda)
                            )
                              .map(symbol => `${symbol} `)
                              .getOrElse(null),
                          },
                          number
                        )
                      )
                      .map(removePad)
                      .getOrElse(valor);
                  };

                  const formataDecimal = (valor, formatacaoResultado) => {
                    const decimals =
                      hasPath(["casasDecimais"], col) &&
                      !isNil(col.casasDecimais)
                        ? col.casasDecimais
                        : casasDecimaisDefault;

                    return maybeEmpty(valor)
                      .map(parseFloat)
                      .chain(onlyNumbers)
                      .map(number => number.toString())
                      .map(number =>
                        formatNumber(
                          {
                            ...pick(
                              ["decimals", "prefix", "suffix"],
                              formatacaoResultado
                            ),
                            decimals,
                          },
                          number
                        )
                      )
                      .getOrElse(valor);
                  };

                  const formataTipo = ({ valor, formatacaoResultado }) => {
                    valor = normalizaValor({ valor, formatacaoResultado });

                    switch (formatacaoResultado.formatacao) {
                      case "PORCENTAGEM":
                      case "PORCENTAGEM_TAXAS_SEM_TEXTOS":
                      case "PORCENTAGEM_TAXAS":
                        return valor;
                      case "DECIMAL":
                        return formataDecimal(
                          escalaFormatacao(
                            valor,
                            pathOr(null, ["escala"], col)
                          ),
                          formatacaoResultado
                        );
                      case "MOEDA":
                      case "MOEDA_QUALITATIVA":
                        return formataMoeda(
                          escalaFormatacao(
                            valor,
                            pathOr(null, ["escala"], col)
                          ),
                          formatacaoResultado
                        );
                      case "INTEIRO":
                        return valor;
                      case "DATA":
                        return valor;
                      default:
                        return valor;
                    }
                  };

                  const valorTestado = parseValueOrJson(value);

                  // Se veio com JSON, passa pelo normalizaValor e formataTipo (novo formato)
                  // Se não, passa variável direto com formatação anterior (pela coluna)
                  if (typeof valorTestado === "object") {
                    return String(formataTipo(valorTestado));
                  } else {
                    return valueFormatted || value;
                  }
                },
              };
            })
          )
        ),
      ].map(col => {
        let valueFormatter = col.valueFormatter;
        if (valueFormatter) {
          valueFormatter.type = col[colType];
        } else if (Array.isArray(col.children)) {
          const colChildren = col.children[0];
          valueFormatter = colChildren.valueFormatter;
          if (valueFormatter) {
            valueFormatter.type = colChildren[colType];
          }
        }

        return col;
      });

      return {
        rows,
        cols,
      };
    }
  )
);

class Grid extends React.Component<Props & I18n<string>, State> {
  gridApi: any;
  columnApi: any;

  state = {
    currentPage: 1,
    pageCount: 0,
    pageSize: 1000,
    pagination: false,
    selectedAssets: [],
  };

  withGridApi: Function => any = fn => Maybe(this.gridApi).map(fn);

  getCustomMenuAction = (context, menuAction) => {
    switch (menuAction) {
      case "exportaGrafico":
        return {
          name: this.props.translate("Comum.Grafico"),
          action: () => {
            try {
              const ultimaSelecao = this.gridApi.getCellRanges().length - 1;

              const primeiraLinha =
                this.gridApi.getCellRanges()[ultimaSelecao].startRow.rowIndex <=
                this.gridApi.getCellRanges()[ultimaSelecao].endRow.rowIndex
                  ? this.gridApi.getCellRanges()[ultimaSelecao].startRow
                  : this.gridApi.getCellRanges()[ultimaSelecao].endRow;

              const ultimaLinha =
                this.gridApi.getCellRanges()[ultimaSelecao].endRow.rowIndex >
                this.gridApi.getCellRanges()[ultimaSelecao].startRow.rowIndex
                  ? this.gridApi.getCellRanges()[ultimaSelecao].endRow
                  : this.gridApi.getCellRanges()[ultimaSelecao].startRow;

              const selecao = {
                colunas: this.gridApi.getCellRanges()[ultimaSelecao].columns,
                primeiraLinha,
                ultimaLinha,
              };

              const linhas = range(
                selecao.primeiraLinha.rowIndex,
                selecao.ultimaLinha.rowIndex + 1
              );

              const dados = selecao.colunas.map(coluna => {
                return {
                  coluna: coluna.colId,
                  datas: linhas.map(
                    linha => this.gridApi.getDisplayedRowAtIndex(linha).id
                  ),
                  formatador: coluna.colDef.valueFormatter.type,
                  medida: coluna.colDef.headerName,
                  valores: linhas.map(linha => {
                    let valor = this.gridApi.getValue(
                      coluna.colId,
                      this.gridApi.getDisplayedRowAtIndex(linha)
                    );

                    if (coluna.colDef.__colType__ === "percentage") {
                      valor = 100 * valor;
                    }

                    return valor === "" ? null : Number(valor);
                  }),
                  valoresFormatados: linhas.map(linha =>
                    coluna.colDef.valueFormatter({
                      value: this.gridApi.getValue(
                        coluna.colId,
                        this.gridApi.getDisplayedRowAtIndex(linha)
                      ),
                    })
                  ),
                };
              });

              this.props.openModal("HighchartsModal", { dados: dados });
            } catch {
              this.props.openModal("ModalErro", {
                erros: [
                  this.props.translate(
                    "configuracaoRelatorio.erroGerarGrafico"
                  ),
                ],
              });
            }
          },
        };

      case "copy":
        return {
          name: this.props.translate("Comum.Copiar.Area.Marcada"),
          action: () =>
            this.withGridApi(gridApi => gridApi.copySelectedRangeToClipboard()),
        };

      case "markSelectedAssets":
        return {
          name: this.props.translate("Relatorio.Marcar.Ativos.Selecionados"),
          action: () =>
            this.withGridApi(gridApi => {
              Maybe(context.node).map(node => node.setSelected(true));

              Maybe(gridApi.getCellRanges())
                .map(
                  map(({ startRow, endRow }) =>
                    range(startRow.rowIndex, endRow.rowIndex + 1)
                  )
                )
                .map(
                  map(rows =>
                    rows.map(row => gridApi.getDisplayedRowAtIndex(row))
                  )
                )
                .map(map(rows => rows.map(row => row.setSelected(true))));
            }),
        };

      case "removeAsset":
        return {
          name: this.props.translate("Ativo.Remover"),
          action: () => this.props.onRemoveAsset(context.node.data.key),
        };

      case "sortAsc":
        return {
          name: this.props.translate("Comum.Ordernar.Crescente"),
          action: () =>
            this.withGridApi(gridApi => {
              gridApi.setSortModel([
                {
                  colId: context.column.colId,
                  sort: "asc",
                },
              ]);
            }),
        };

      case "sortDesc":
        return {
          name: this.props.translate("Comum.Ordernar.Decrescente"),
          action: () =>
            this.withGridApi(gridApi => {
              gridApi.setSortModel([
                {
                  colId: context.column.colId,
                  sort: "desc",
                },
              ]);
            }),
        };

      case "selectThisColumn":
        return {
          name: this.props.translate("Relatorio.Selecionar.Toda.Coluna"),
          action: () => {
            this.gridApi.clearRangeSelection();
            return this.withGridApi(gridApi =>
              gridApi.addCellRange({
                columnStart: context.column,
                columnEnd: context.column,
                rowStartIndex: 0,
                rowEndIndex: this.props.rows.length - 1,
              })
            );
          },
        };

      default:
        return null;
    }
  };

  getMainMenuItems = params => [
    this.getCustomMenuAction(params, "selectThisColumn"),
    "separator",
    this.getCustomMenuAction(params, "copy"),
    "separator",
    "autoSizeThis",
    "autoSizeAll",
    "separator",
    "resetColumns",
  ];

  onSearch = (event: *) =>
    this.withGridApi(api => api.setQuickFilter(event.target.value));

  setPaginationInfo = () => {
    this.withGridApi(api =>
      this.setState({
        currentPage: api.paginationGetCurrentPage(),
        pageCount: api.paginationGetTotalPages(),
      })
    );
  };

  onCSVExport = () => this.props.exportReportAsCSV(this.props.reportId);

  onExcelExport = () => this.props.exportReportAsExcel(this.props.reportId);

  onLinkedExcelExport = () => {
    this.props.exportarExcelVinculado();
  };

  onSaveGroupClick = () => this.props.openModal("saveAssetGroup");

  onSaveGroupSubmit = ({ name, override = false }) =>
    this.props.saveAssetGroup({
      group: this.state.selectedAssets,
      name,
      override,
    });

  onSelectPage = (event: *) => {
    const {
      target: { value },
    } = event;

    event.preventDefault();
    this.withGridApi(api => api.paginationGoToPage(parseInt(value, 10)));
  };

  onSendMailClick = () => this.props.openModal("sendMail");

  onSendMailSubmit = mail => this.props.sendMail(this.props.reportId, mail);

  onPageSizeChange = (event: *) => {
    const value = event.target.value;

    if (!value) {
      this.setState({ pagination: false });
    } else {
      this.withGridApi(api => {
        api.paginationSetPageSize(parseInt(value, 10));
      });
    }
  };

  onPrevPageClick = (event: *) => {
    event.preventDefault();
    this.withGridApi(api => api.paginationGoToPreviousPage());
  };

  onNextPageClick = (event: *) => {
    event.preventDefault();
    this.withGridApi(api => api.paginationGoToNextPage());
  };

  onGridReady = (event: *) => {
    this.gridApi = event.api;
    this.columnApi = event.columnApi;
    this.setPaginationInfo();
    this.restoreGridFromState();
  };

  onGridSelectionChange = () => {
    this.withGridApi(gridApi => {
      this.setState({
        selectedAssets: gridApi.getSelectedNodes().map(node => node.data.key),
      });
    });
  };

  onSelectionRemove = () =>
    this.props.onRemoveAssets(this.state.selectedAssets);

  restoreGridFromState = () => {
    const { filter, fixedLeft, fixedRight, order, sort } = this.props.gridState;

    const convertFilterModel = ({
      filter,
      filterType,
      type,
      operator,
      filters,
      dateFrom,
      dateTo,
    }) => {
      switch (filterType) {
        case "COMPOSITION":
          return {
            operator,
            condition1: filters[0],
            condition2: filters[1],
          };

        case "date":
          return {
            filter,
            filterType,
            type,
            dateFrom,
            dateTo,
          };

        default:
          return {
            filter,
            filterType,
            type,
          };
      }
    };

    const filterModel = filter.reduce(
      (model, { column, ...filter }) => ({
        ...model,
        [column]: convertFilterModel(filter),
      }),
      {}
    );

    const sortModel = sort.reduce(
      (model, [colId, sort]) => [...model, { colId, sort }],
      []
    );

    const fixedColumns = {
      ...fixedLeft.reduce(
        (fixed, colId) => ({
          ...fixed,
          [colId]: "left",
        }),
        {}
      ),
      ...fixedRight.reduce(
        (fixed, colId) => ({
          ...fixed,
          [colId]: "right",
        }),
        {}
      ),
    };

    const isHidden = colId => !order.find(equals(colId)) && order.length !== 0;

    const columns = this.columnApi.getColumnState().map(column => ({
      ...column,
      hide: isHidden(column.colId),
      pinned: fixedColumns[column.colId],
    }));

    this.columnApi.setColumnState(columns);

    if (order.length > 0) {
      const columnById = indexBy(
        prop("colId"),
        this.columnApi.getColumnState()
      );

      const [validCols, invalidCols] = partition(
        colId => colId in columnById,
        order
      );

      if (invalidCols.length > 0) {
        console.warn("Visualização contém colunas inválidas: ", invalidCols);
      }

      const orderedColumnState = validCols.map(colId => columnById[colId]);
      this.columnApi.setColumnState(orderedColumnState);
    }

    this.gridApi.setFilterModel(filterModel);
    this.gridApi.setSortModel(sortModel);
  };

  onSaveColumnState = ({ type, columns }) => {
    const { saveGridState } = this.props;
    const columnState = this.columnApi.getColumnState();

    const persistOrder = () =>
      saveGridState(
        "order",
        columnState.filter(({ hide }) => !hide).map(prop("colId"))
      );

    switch (type) {
      case "columnVisible":
      case "columnMoved": {
        persistOrder();
        break;
      }

      case "columnPinned": {
        const fixed = pipe(
          groupBy(prop("pinned")),
          map(map(prop("colId")))
        )(columnState);

        saveGridState("fixedRight", fixed["right"] || []);
        saveGridState("fixedLeft", fixed["left"] || []);

        break;
      }

      default:
        break;
    }
  };

  onSortChanged = params => {
    const sortModel = params.api.getSortModel();

    const sort = sortModel.reduce(
      (model, s) => [...model, [s.colId, s.sort]],
      []
    );

    this.props.saveGridState("sort", sort);
  };

  onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();

    const convertFilterModel = filter => {
      if (filter.operator != null) {
        return {
          filterType: "COMPOSITION",
          operator: filter.operator,
          filters: [filter.condition1, filter.condition2],
        };
      }

      switch (filter.filterType) {
        case "formattedNumber": {
          return {
            filterType: "number",
            type: filter.type,
            filter: filter.filter,
          };
        }

        default:
          return filter;
      }
    };

    const filter = toPairs(filterModel)
      .filter(([, filter]) => !isEmpty(filter.filter))
      .map(([colId, filter]) => ({
        column: colId,
        ...convertFilterModel(filter),
      }));

    this.props.saveGridState("filter", filter);
  };

  onCopyToClipboard = params => {
    const { value } = params;

    if (value instanceof Date) {
      return moment(value).format("DD/MM/YYYY");
    }

    return value;
  };

  onFirstDataRendered = () => {
    this.restoreGridFromState();
  };

  menuItems = (params: *) => {
    return [
      this.getCustomMenuAction(params, "exportaGrafico"),
      "separator",
      "copy",
      "copyWithHeaders",
      "separator",
      this.getCustomMenuAction(params, "selectThisColumn"),
      "separator",
      // "rangeLineChart",
      //    Retirado o suporte de gráfico do ag-grid para utilização do HighCharts
    ];
  };

  processChartOptions = ({ options, type }) => {
    options.legend.position = "bottom";

    const columns = this.withGridApi(api =>
      api.getCellRanges().flatMap(range => range.columns)
    ).getOrElse([]);

    const series = columns.filter(
      column => column.colDef.chartDataType === "series"
    );

    const types = series.map(col => col.colDef[colType]);

    const formatDate = date =>
      parseDate(date)
        .map(date => moment(date).format("DD/MM/YYYY"))
        .getOrElse(null);

    if (type !== "pie" && type !== "doughnut") {
      //Evitar Crash
      options.seriesDefaults.fill.opacity = 0;
      options.legend.item.marker.strokeWidth = 9;
      options.legend.item.marker.size = 5;

      options.xAxis.label.rotation = 315;
      options.xAxis.label.formatter = ({ value }) => {
        if (value instanceof Date) {
          return formatDate(value);
        } else {
          if (value instanceof Object) {
            if (value.value instanceof Date) {
              return formatDate(value.value);
            } else {
              return value.value;
            }
          } else {
            return value;
          }
        }
      };

      options.yAxis.label.formatter = ({ value }) => value;

      // Verificamos se todas as colunas tem o mesmo tipo, já que caso contrário,
      // é impossível ter um valor formatado para o eixo y
      if (uniq(types).length === 1) {
        if (series[0].colDef.valueFormatter) {
          options.yAxis.label.formatter = params => {
            if (series[0].colDef.valueFormatter(params)) {
              return series[0].colDef.valueFormatter(params);
            } else {
              if (params.value.value) {
                return formatDate(params.value.value);
              } else {
                return 0;
              }
            }
          };
        }
      }

      options.seriesDefaults.tooltip.renderer = params =>
        renderToStaticMarkup(
          <ChartTooltip
            {...params}
            formatX={options.xAxis.label.formatter}
            formatY={options.yAxis.label.formatter}
          />
        );
    }
    return options;
  };

  localeTextFunc = (key, defaultValue) => {
    switch (key) {
      //   Nessa versão do ag-grid o gráfico de linhas está bugado na
      // escala do eixo Y. Estamos usando o gráfico de área e retirando
      // a opacidade para imitar o gráfico de linha.
      case "area":
        return "Linha";
      default:
        return this.props.lookup(`ag-grid.${key}`) || defaultValue;
    }
  };

  render() {
    const { loaded, rows, cols } = this.props;

    return (
      <div className="Grid">
        <Header onSearch={this.onSearch} />

        <Filters
          currentPage={this.state.currentPage}
          pageCount={this.state.pageCount}
          pagination={this.state.pagination}
          removeFromSelectionDisabled={this.state.selectedAssets.length === 0}
          saveGroupDisabled={this.state.selectedAssets.length === 0}
          openFileDisabled={this.state.selectedAssets.length > 0}
          saveFileDisabled={this.state.selectedAssets.length > 0}
          onCSVExport={this.onCSVExport}
          onExcelExport={this.onExcelExport}
          onLinkedExcelExport={this.onLinkedExcelExport}
          onOpenNewTask={this.props.onOpenNewTask}
          onSaveGroupClick={this.onSaveGroupClick}
          onSaveGroupSubmit={this.onSaveGroupSubmit}
          onSendMailClick={this.onSendMailClick}
          onSendMailSubmit={this.onSendMailSubmit}
          onNextPageClick={this.onNextPageClick}
          onPageSizeChange={this.onPageSizeChange}
          onPrevPageClick={this.onPrevPageClick}
          onSelectPage={this.onSelectPage}
          onSelectionRemove={this.onSelectionRemove}
        />

        <div className="ag-theme-balham Grid__grid">
          {loaded && (
            <AgGridReact
              processCellForClipboard={this.onCopyToClipboard}
              aggFuncs={aggregationFunctions}
              onFirstDataRendered={this.onFirstDataRendered}
              onColumnVisible={this.onSaveColumnState}
              onColumnPinned={this.onSaveColumnState}
              onColumnResized={this.onSaveColumnState}
              onColumnMoved={this.onSaveColumnState}
              processChartOptions={this.processChartOptions}
              onGridReady={this.onGridReady}
              onFilterChanged={this.onFilterChanged}
              onSortChanged={this.onSortChanged}
              filterRow={{ visible: false }}
              filterPanel={{ visible: false }}
              enableRangeSelection
              enableCharts
              rowGroupPanelShow="never"
              getMainMenuItems={this.getMainMenuItems}
              groupIncludeFooter
              floatingFilter
              suppressPaginationPanel
              suppressRowClickSelection
              pagination={this.state.pagination}
              onPaginationChanged={this.setPaginationInfo}
              paginationPageSize={this.state.pageSize}
              rowSelection="multiple"
              onSelectionChanged={this.onGridSelectionChange}
              multiSortKey="shift"
              headerHeight={50}
              groupHeaderHeight={25}
              floatingFiltersHeight={25}
              pivotHeaderHeight={25}
              pivotGroupHeaderHeight={25}
              rowHeight={22}
              deltaRowDataMode
              getRowNodeId={data => data.rowId}
              modules={[ClipboardModule]}
              getContextMenuItems={this.menuItems}
              popupParent={document.body}
              defaultColDef={defaultColDef}
              columnDefs={cols}
              rowData={rows}
              localeTextFunc={this.localeTextFunc}
              statusBar={{
                statusPanels: [
                  {
                    statusPanel: "agTotalAndFilteredRowCountComponent",
                    key: "totalAndFilter",
                    align: "left",
                  },
                  { statusPanel: "agSelectedRowCountComponent", align: "left" },
                  {
                    statusPanel: "aggregationStatusBar",
                    align: "right",
                    statusPanelParams: {
                      aggFuncs: [
                        "avg",
                        "median",
                        "count",
                        "min",
                        "max",
                        "minDate",
                        "maxDate",
                        "sum",
                      ],
                    },
                  },
                ],
              }}
              frameworkComponents={{
                aggregationStatusBar: AggregationStatusBar,
                formattedNumberFilter: FormattedNumberFilter,
                proxyFloatingFilter: ProxyFloatingFilter,
                agDateInput: props => (
                  <DatePicker translate={this.props.translate} {...props} />
                ),
              }}
            />
          )}
        </div>
      </div>
    );
  }
}

export default enhance(Grid);
